import { gql, useMutation } from "@apollo/client";
import React, { useState } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import Swal from "sweetalert2";
import InputTypePassword from "../../../Components/InputTypePassword";
import { EMAIL_REGEX, PASSWORD_REGEX } from "../../../Utils/Constants";
import StudentConversionModal from "./StudentConversionModal";

const passwordError =
  "La contraseña debe tener mínimo entre 8 y 15 caracteres, una letra minúscula, una mayúscula, un número y un carácter especial.";

const estadoOptions = [
  { key: "ACTIVO", value: "ACTIVO", text: "Activo" },
  { key: "INACTIVO", value: "INACTIVO", text: "Inactivo" },
];

const UpdateAdmin = ({ trigger, admin, optionsRoles = [], reload }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [document, setDocument] = useState(admin?.documento || "");
  const [firstName, setFirstName] = useState(admin?.primerNombre || "");
  const [firstSurname, setFirstSurname] = useState(admin?.primerApellido || "");
  const [email, setEmail] = useState(admin?.email || "");
  const [password, setPassword] = useState("");
  const [statusCheck, setStatusCheck] = useState("profile");
  const [idGrupoUsuario, setIdGrupoUsuario] = useState(
    admin?.multiGrupoUsuario?.id || "",
  );
  const [userEstado, setUserEstado] = useState(admin?.estado || "ACTIVO");
  const [showStudentModal, setShowStudentModal] = useState(false);

  const onCompleted = () => {
    setLoading(false);
    setPassword("");
    const text =
      statusCheck === "profile"
        ? "Se ha actualizado el usuario exitosamente."
        : "Se ha actualizado la contraseña del usuario exitosamente.";
    Swal.fire({ icon: "success", title: "Completado", text }).then(() => {
      if (reload) reload();
      setOpen(false);
    });
  };

  const onError = ({ graphQLErrors }) => {
    setLoading(false);
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: graphQLErrors?.[0]?.message || "Lo sentimos ha ocurrido un error.",
    });
  };

  const [mutation] = useMutation(UPDATE_ADMIN, { onCompleted, onError });

  const handleMutation = () => {
    if (statusCheck === "profile" && idGrupoUsuario === "2") {
      setShowStudentModal(true);
      return;
    }

    setLoading(true);
    const input = { id: admin?.id };

    if (statusCheck === "profile") {
      if (document) input.documento = document;
      if (firstName) input.primerNombre = firstName;
      if (firstSurname) input.primerApellido = firstSurname;
      if (email) input.email = email;
      if (idGrupoUsuario && idGrupoUsuario !== admin?.multiGrupoUsuario?.id)
        input.idGrupoUsuario = idGrupoUsuario;
      if (userEstado && userEstado !== admin?.estado) input.estado = userEstado;
    }

    if (statusCheck === "password") {
      input.password = password;
    }

    mutation({ variables: { input } });
  };

  const disabled = () => {
    if (statusCheck === "profile")
      return (
        !document.trim() ||
        !firstName.trim() ||
        !firstSurname.trim() ||
        !EMAIL_REGEX.test(email)
      );
    if (statusCheck === "password") return !PASSWORD_REGEX.test(password);
  };

  const handleClose = () => {
    setDocument(admin?.documento || "");
    setFirstName(admin?.primerNombre || "");
    setFirstSurname(admin?.primerApellido || "");
    setEmail(admin?.email || "");
    setIdGrupoUsuario(admin?.multiGrupoUsuario?.id || "");
    setUserEstado(admin?.estado || "ACTIVO");
    setPassword("");
    setStatusCheck("profile");
    setOpen(false);
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: () => setOpen(true) })}
      <Modal size="tiny" onClose={handleClose} open={open} dimmer="inverted">
        <Modal.Header>Actualizar usuario</Modal.Header>
        <Modal.Content>
          <div style={{ textAlign: "center" }}>
            <Button
              style={{ color: "white", backgroundColor: "#045A73" }}
              onClick={() => setStatusCheck("profile")}
              content="Cambiar perfil"
            />
            <Button
              style={{ color: "white", backgroundColor: "#045A73" }}
              onClick={() => setStatusCheck("password")}
              content="Cambiar contraseña"
            />
          </div>

          {statusCheck === "profile" && (
            <Form>
              <Form.Input
                value={document}
                label="Documento"
                onChange={(e, { value }) => setDocument(value)}
              />
              <Form.Input
                value={firstName}
                label="Primer nombre"
                onChange={(e, { value }) => setFirstName(value)}
              />
              <Form.Input
                value={firstSurname}
                label="Primer apellido"
                onChange={(e, { value }) => setFirstSurname(value)}
              />
              <Form.Input
                value={email}
                label="Correo"
                error={
                  email?.trim() && !EMAIL_REGEX.test(email)
                    ? "Ingresa una dirección de correo electrónico válida."
                    : null
                }
                onChange={(e, { value }) => setEmail(value)}
              />
              <Form.Dropdown
                label="Grupo usuario"
                selection
                value={idGrupoUsuario}
                options={optionsRoles}
                onChange={(e, { value }) => setIdGrupoUsuario(value)}
              />
              <Form.Dropdown
                label="Estado"
                selection
                value={userEstado}
                options={estadoOptions}
                onChange={(e, { value }) => setUserEstado(value)}
              />
            </Form>
          )}

          {statusCheck === "password" && (
            <Form>
              <InputTypePassword
                label="Contraseña"
                value={password}
                error={
                  password.trim() && !PASSWORD_REGEX.test(password)
                    ? passwordError
                    : null
                }
                onChange={(e, { value }) => setPassword(value)}
              />
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            style={{ color: "white", backgroundColor: "#045A73" }}
            loading={loading}
            disabled={disabled() || loading}
            onClick={handleMutation}
            content="Guardar"
          />
          <Button
            style={{ backgroundColor: "#b0193f", color: "white" }}
            onClick={handleClose}
            content="Cancelar"
          />
        </Modal.Actions>
      </Modal>

      {showStudentModal && (
        <StudentConversionModal
          open={showStudentModal}
          adminId={admin?.id}
          basicInput={{
            documento: document,
            primerNombre: firstName,
            primerApellido: firstSurname,
            email,
            estado: userEstado !== admin?.estado ? userEstado : undefined,
          }}
          onCompleted={() => {
            setShowStudentModal(false);
            if (reload) reload();
            setOpen(false);
            Swal.fire({
              icon: "success",
              title: "Completado",
              text: "El usuario ha sido convertido a Estudiante exitosamente.",
            });
          }}
          onCancel={() => setShowStudentModal(false)}
        />
      )}
    </>
  );
};

const UPDATE_ADMIN = gql`
  mutation updateAdmin($input: ActualizarMultiUsuarioAdminInput!) {
    actualizarAdmin(input: $input) {
      id
      nombre
      documento
      primerNombre
      primerApellido
      email
      estado
      multiGrupoUsuario {
        id
        nombre
      }
    }
  }
`;

export default UpdateAdmin;
